import { Button, EmptyState, Icon, useConfirm, useToast } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { apiCall } from '@/lib/api';
import { createMutation, useQueryClient } from '@tanstack/solid-query';
import {
  ApiClientError,
  type DeviceFile,
  type DeviceFileGroup,
  type SupportedContentType,
} from '@uchat-team-management/shared';
import { For, type JSX, Show, createSignal } from 'solid-js';
import { DeviceImage } from './DeviceImage';
import { Lightbox } from './Lightbox';
import { deleteDeviceFile, reorderDeviceFile, uploadDeviceFile } from './api';

interface Props {
  deviceId: string;
  files: readonly DeviceFile[];
  fileGroups: readonly DeviceFileGroup[];
  assignedUserId: string | null;
}

export const DeviceFiles = (props: Props): JSX.Element => {
  return (
    <section class="rounded-xl border border-neutral-100 bg-surface p-4 shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
      <h2 class="mb-4 text-base font-semibold">Files</h2>
      <div class="flex flex-col gap-6">
        <For each={props.fileGroups}>
          {(group) => (
            <GroupSection
              deviceId={props.deviceId}
              group={group}
              files={props.files.filter((f) => f.groupId === group.id)}
              assignedUserId={props.assignedUserId}
            />
          )}
        </For>
      </div>
    </section>
  );
};

const GroupSection = (props: {
  deviceId: string;
  group: DeviceFileGroup;
  files: readonly DeviceFile[];
  assignedUserId: string | null;
}) => {
  const { session } = useAuth();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const [uploading, setUploading] = createSignal(false);
  let fileInputRef: HTMLInputElement | undefined;

  const accessAll = () => session()?.permissions.has('devices:files:accessAll') ?? false;
  const ownsDevice = () => session()?.id === props.assignedUserId;
  const canUpload = () => {
    const perm = `devicefiles:${props.group.slug}:upload`;
    if (!session()?.permissions.has(perm)) return false;
    return accessAll() || ownsDevice();
  };
  const canDelete = () => {
    const perm = `devicefiles:${props.group.slug}:delete`;
    if (!session()?.permissions.has(perm)) return false;
    return accessAll() || ownsDevice();
  };

  const atCap = () => props.files.length >= props.group.perDeviceCap;

  const upload = createMutation(() => ({
    mutationFn: (file: File) => uploadDeviceFile(props.deviceId, props.group.slug, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices', props.deviceId] });
      qc.invalidateQueries({ queryKey: ['devices'] });
    },
  }));

  const remove = createMutation(() => ({
    mutationFn: (fileId: string) =>
      apiCall(deleteDeviceFile, { params: { deviceId: props.deviceId, fileId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices', props.deviceId] }),
  }));

  const reorder = createMutation(() => ({
    mutationFn: (input: { fileId: string; sortOrder: number }) =>
      apiCall(reorderDeviceFile, {
        params: { deviceId: props.deviceId, fileId: input.fileId },
        body: { sortOrder: input.sortOrder },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices', props.deviceId] });
      qc.invalidateQueries({ queryKey: ['devices'] });
    },
  }));

  // Drag state is scoped to this group section, so a drop originating in
  // another group never matches and cross-group moves are a natural no-op.
  const [draggingId, setDraggingId] = createSignal<string | null>(null);
  const [lightbox, setLightbox] = createSignal<DeviceFile | null>(null);

  const onDrop = async (destFile: DeviceFile) => {
    const sourceId = draggingId();
    setDraggingId(null);
    if (!sourceId || sourceId === destFile.id) return;
    try {
      await reorder.mutateAsync({ fileId: sourceId, sortOrder: destFile.sortOrder });
    } catch {
      toast.error('Reorder failed');
    }
  };

  const onSetCover = async (file: DeviceFile) => {
    try {
      await reorder.mutateAsync({ fileId: file.id, sortOrder: 0 });
      toast.success('Cover updated');
    } catch {
      toast.error('Set cover failed');
    }
  };

  const onFileChosen = async (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        try {
          await upload.mutateAsync(file);
          toast.success(`${file.name} uploaded`);
        } catch (err) {
          if (err instanceof ApiClientError) toast.error(`${file.name}: ${err.error.message}`);
          else toast.error(`${file.name}: upload failed`);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (file: DeviceFile) => {
    const ok = await confirmDialog({
      title: `Delete ${file.filename}?`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(file.id);
      toast.success('File deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div>
      <div class="mb-2 flex items-center justify-between">
        <h3 class="text-sm font-medium">
          {props.group.name}{' '}
          <span class="text-xs text-neutral-500">
            ({props.files.length}/{props.group.perDeviceCap})
          </span>
        </h3>
        <Show when={canUpload()}>
          <Button
            size="sm"
            variant="ghost"
            disabled={atCap() || uploading()}
            loading={uploading()}
            onClick={() => fileInputRef?.click()}
          >
            {atCap() ? 'Cap reached' : '+ Upload'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            class="hidden"
            accept={props.group.allowedContentTypes.join(',')}
            onChange={onFileChosen}
          />
        </Show>
      </div>
      <Show
        when={props.files.length > 0}
        fallback={
          <EmptyState title="No files" body={`No ${props.group.name.toLowerCase()} yet.`} />
        }
      >
        <ul class="flex flex-wrap gap-3">
          <For each={props.files}>
            {(file) => (
              <li
                class="group relative"
                draggable={canUpload()}
                onDragStart={() => setDraggingId(file.id)}
                onDragEnd={() => setDraggingId(null)}
                onDragOver={(e) => {
                  if (draggingId()) e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  onDrop(file);
                }}
              >
                <Show
                  when={file.contentType.startsWith('image/')}
                  fallback={
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex h-24 w-32 flex-col items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 text-xs hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800"
                    >
                      <Icon name="document" class="h-6 w-6" />
                      <span class="mt-1 max-w-[7rem] truncate px-1 text-center">
                        {file.filename}
                      </span>
                    </a>
                  }
                >
                  <button
                    type="button"
                    onClick={() => setLightbox(file)}
                    aria-label={file.filename}
                  >
                    <DeviceImage src={file.url} alt={file.filename} class="h-24 w-24 rounded-lg" />
                  </button>
                </Show>
                <Show
                  when={
                    props.group.slug === 'photos' &&
                    file.contentType.startsWith('image/') &&
                    file.sortOrder !== 0 &&
                    canUpload()
                  }
                >
                  <button
                    type="button"
                    class="absolute inset-x-1 bottom-1 hidden rounded bg-black/60 px-1 py-0.5 text-[10px] text-white group-hover:block"
                    onClick={() => onSetCover(file)}
                  >
                    Set as cover
                  </button>
                </Show>
                <Show when={canDelete()}>
                  <button
                    type="button"
                    class="absolute -right-2 -top-2 hidden rounded-full bg-red-600 p-1 text-xs text-white group-hover:block"
                    onClick={() => onDelete(file)}
                    aria-label="Delete file"
                  >
                    <Icon name="cross-small" class="h-3 w-3" />
                  </button>
                </Show>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Lightbox file={lightbox()} onClose={() => setLightbox(null)} />
    </div>
  );
};
